import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete, Form, Input, InputNumber, Row, Col } from "antd";
import { MapContainer, TileLayer, Marker, Circle, CircleMarker, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { Localize } from "components/service";
import { useDebounce } from "components/use-hooks";
import "leaflet/dist/leaflet.css";

const LEIPZIG_CENTER = [ 51.3396, 12.3712 ];
const DEFAULT_CHECK_RADIUS = 50;
const DEFAULT_ZOOM = 12;
const CHECK_ZOOM = 16;
const ORIGIN_COORDINATE_THRESHOLD = 0.01;
const PHOTON_SEARCH_URL = "https://photon.komoot.io/api/";
const PHOTON_COUNTRY_CODE = "DE";
const PHOTON_LANGUAGE = "de";
const PHOTON_LIMIT = 5;
const PHOTON_MIN_QUERY_LENGTH = 3;
const PHOTON_RATE_LIMIT_STATUS = 429;

const { Search } = Input;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const isValidLatitude = (value) => {
    const number = toNumber(value);
    return number !== null && number >= -90 && number <= 90;
};

const isValidLongitude = (value) => {
    const number = toNumber(value);
    return number !== null && number >= -180 && number <= 180;
};

const isNearOrigin = (latitude, longitude) => (
    Math.abs(toNumber(latitude) || 0) < ORIGIN_COORDINATE_THRESHOLD &&
    Math.abs(toNumber(longitude) || 0) < ORIGIN_COORDINATE_THRESHOLD
);

const hasMeaningfulCoordinates = (latitude, longitude) => (
    isValidLatitude(latitude) &&
    isValidLongitude(longitude) &&
    !isNearOrigin(latitude, longitude)
);

const roundCoordinate = (value) => Number(value.toFixed(6));
const normalizeSearchQuery = (value) => (
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase()
);

const getPhotonCoordinates = (feature) => {
    const coordinates = feature?.geometry?.coordinates || [];
    const longitude = toNumber(coordinates[ 0 ]);
    const latitude = toNumber(coordinates[ 1 ]);

    return isValidLatitude(latitude) && isValidLongitude(longitude) ? [ latitude, longitude ] : null;
};

const getPhotonExtentBounds = (feature) => {
    const extent = feature?.properties?.extent || [];

    if (!Array.isArray(extent) || extent.length < 4)
    {
        return null;
    }

    const west = toNumber(extent[ 0 ]);
    const firstLatitude = toNumber(extent[ 1 ]);
    const east = toNumber(extent[ 2 ]);
    const secondLatitude = toNumber(extent[ 3 ]);

    if (
        !isValidLongitude(west) ||
        !isValidLatitude(firstLatitude) ||
        !isValidLongitude(east) ||
        !isValidLatitude(secondLatitude)
    )
    {
        return null;
    }

    return [
        [ firstLatitude, west ],
        [ secondLatitude, east ],
    ];
};

const getPhotonFeatureLabel = (feature) => {
    const properties = feature?.properties || {};
    const cityLine = [ properties.postcode, properties.city || properties.locality || properties.county ].filter(Boolean).join(" ");
    const addressParts = [
        properties.street && properties.housenumber ? `${ properties.street } ${ properties.housenumber }` : properties.street,
        cityLine,
        properties.state,
        properties.country,
    ].filter(Boolean);
    const address = addressParts.join(", ");
    const name = properties.name || address;

    return name && address && name !== address ? `${ name } - ${ address }` : (name || address);
};

const mapPhotonFeatureToOption = (feature, index) => {
    const center = getPhotonCoordinates(feature);
    const label = getPhotonFeatureLabel(feature);

    if (!center || !label)
    {
        return null;
    }

    return {
        value: label,
        label,
        feature,
        key: `${ feature?.properties?.osm_type || "feature" }-${ feature?.properties?.osm_id || index }-${ center.join(",") }`,
    };
};

const getPhotonOptionScore = (option, normalizedQuery) => {
    const properties = option?.feature?.properties || {};
    const normalizedName = normalizeSearchQuery(properties.name);
    const normalizedLabel = normalizeSearchQuery(option?.label);
    const queryWords = normalizedQuery.split(" ").filter(Boolean);

    let score = 0;

    if (normalizedName === normalizedQuery)
    {
        score += 100;
    }

    if (normalizedName.includes(normalizedQuery))
    {
        score += 80;
    }

    queryWords.forEach((word) => {
        if (normalizedName.includes(word))
        {
            score += 15;
        }

        if (normalizedLabel.includes(word))
        {
            score += 5;
        }
    });

    if (properties.extent?.length)
    {
        score += 3;
    }

    return score;
};

const sortPhotonOptions = (options, normalizedQuery) => (
    [ ...options ].sort((firstOption, secondOption) => (
        getPhotonOptionScore(secondOption, normalizedQuery) - getPhotonOptionScore(firstOption, normalizedQuery)
    ))
);

const MapViewController = ({ center, zoom }) => {
    const map = useMap();

    useEffect(() => {
        map.setView(center, zoom);
        window.setTimeout(() => map.invalidateSize(), 0);
    }, [ map, center, zoom ]);

    return null;
};

const MapSearchController = ({ target }) => {
    const map = useMap();

    useEffect(() => {
        if (!target)
        {
            return;
        }

        if (target.bounds?.length)
        {
            map.fitBounds(L.latLngBounds(target.bounds), { padding: [ 30, 30 ] });
        }
        else if (target.center)
        {
            map.setView(target.center, target.zoom || CHECK_ZOOM);
        }

        window.setTimeout(() => map.invalidateSize(), 0);
    }, [ map, target ]);

    return null;
};

const MapClickHandler = ({ onMapClick }) => {
    useMapEvents({
        click: onMapClick,
    });

    return null;
};

const MapBoundsController = ({ positions }) => {
    const map = useMap();

    useEffect(() => {
        if (!positions?.length)
        {
            return;
        }

        if (positions.length === 1)
        {
            map.setView(positions[ 0 ], CHECK_ZOOM);
        }
        else
        {
            map.fitBounds(L.latLngBounds(positions), { padding: [ 30, 30 ] });
        }

        window.setTimeout(() => map.invalidateSize(), 0);
    }, [ map, positions ]);

    return null;
};

const ExhibitionLocationMap = ({ event, form }) => {
    const [ searchText, setSearchText ] = useState("");
    const [ searchOptions, setSearchOptions ] = useState([]);
    const [ searchLoading, setSearchLoading ] = useState(false);
    const [ searchTarget, setSearchTarget ] = useState();
    const [ searchUnavailableReasonKey, setSearchUnavailableReasonKey ] = useState();
    const debouncedSearchText = useDebounce(searchText);
    const searchCacheRef = useRef({});
    const searchAbortControllerRef = useRef();
    const selectedSearchTextRef = useRef("");
    const latitude = Form.useWatch("check_latitude", form);
    const longitude = Form.useWatch("check_longitude", form);
    const radius = Form.useWatch("check_radius", form);

    const eventHasCoordinates = hasMeaningfulCoordinates(event?.check_latitude, event?.check_longitude);
    const hasCoordinates = hasMeaningfulCoordinates(latitude, longitude);
    const radiusValue = toNumber(radius);
    const searchDisabled = !!searchUnavailableReasonKey;

    const center = useMemo(() => (
        hasCoordinates ?
            [ toNumber(latitude), toNumber(longitude) ] :
            LEIPZIG_CENTER
    ), [ hasCoordinates, latitude, longitude ]);

    const handleMapClick = useCallback(({ latlng }) => {
        form.setFieldsValue({
            check_latitude: roundCoordinate(latlng.lat),
            check_longitude: roundCoordinate(latlng.lng),
            check_radius: radiusValue || DEFAULT_CHECK_RADIUS,
        });
    }, [ form, radiusValue ]);

    useEffect(() => {
        const normalizedQuery = normalizeSearchQuery(debouncedSearchText);

        if (searchAbortControllerRef.current)
        {
            searchAbortControllerRef.current.abort();
        }

        if (
            searchDisabled ||
            normalizedQuery.length < PHOTON_MIN_QUERY_LENGTH ||
            normalizedQuery === normalizeSearchQuery(selectedSearchTextRef.current)
        )
        {
            setSearchLoading(false);
            setSearchOptions([]);
            return;
        }

        if (searchCacheRef.current[ normalizedQuery ])
        {
            setSearchOptions(searchCacheRef.current[ normalizedQuery ]);
            setSearchLoading(false);
            return;
        }

        const abortController = new AbortController();
        const params = new URLSearchParams({
            q: normalizedQuery,
            countrycode: PHOTON_COUNTRY_CODE,
            limit: String(PHOTON_LIMIT),
            lang: PHOTON_LANGUAGE,
        });

        searchAbortControllerRef.current = abortController;
        setSearchLoading(true);

        fetch(`${ PHOTON_SEARCH_URL }?${ params.toString() }`, { signal: abortController.signal })
            .then((response) => {
                if (!response.ok)
                {
                    const error = new Error("Photon search failed");
                    error.status = response.status;
                    throw error;
                }

                return response.json();
            })
            .then((data) => {
                if (abortController.signal.aborted)
                {
                    return;
                }

                const options = (data?.features || [])
                    .map(mapPhotonFeatureToOption)
                    .filter(Boolean);
                const sortedOptions = sortPhotonOptions(options, normalizedQuery);

                searchCacheRef.current[ normalizedQuery ] = sortedOptions;
                setSearchOptions(sortedOptions);
            })
            .catch((error) => {
                if (error.name === "AbortError")
                {
                    return;
                }

                setSearchOptions([]);
                setSearchUnavailableReasonKey(
                    error.status === PHOTON_RATE_LIMIT_STATUS ?
                        "SEARCH.Error_CheckLocationRateLimit" :
                        "SEARCH.Error_CheckLocationUnavailable"
                );
            })
            .finally(() => {
                if (!abortController.signal.aborted)
                {
                    setSearchLoading(false);
                }
            });

        return () => abortController.abort();
    }, [ debouncedSearchText, searchDisabled ]);

    const handleSearchTextChange = useCallback((value) => {
        if (normalizeSearchQuery(value) !== normalizeSearchQuery(selectedSearchTextRef.current))
        {
            selectedSearchTextRef.current = "";
        }

        setSearchText(value);
    }, []);

    const handleSearchSelect = useCallback((value, option) => {
        const feature = option?.feature;
        const center = getPhotonCoordinates(feature);

        if (!center)
        {
            return;
        }

        selectedSearchTextRef.current = value;
        setSearchText(value);
        setSearchOptions([]);
        setSearchTarget({
            center,
            bounds: getPhotonExtentBounds(feature),
            id: `${ value }-${ Date.now() }`,
            zoom: CHECK_ZOOM,
        });
    }, []);

    const handleSearchSubmit = useCallback(() => {
        if (searchOptions[ 0 ])
        {
            handleSearchSelect(searchOptions[ 0 ].value, searchOptions[ 0 ]);
        }
    }, [ handleSearchSelect, searchOptions ]);

    const searchNotFoundContent = normalizeSearchQuery(searchText).length >= PHOTON_MIN_QUERY_LENGTH && !searchLoading ?
        <span className="event-location-map__search-empty"><Localize>SEARCH.Empty_CheckLocation</Localize></span> :
        null;

    return (
        <div className="event-location-map">
            <div className="event-location-map__search">
                <Form.Item label={ <Localize>FORMS.Input_Label_CheckLocationSearch</Localize> }>
                    <AutoComplete
                        className="event-location-map__search-autocomplete"
                        value={ searchText }
                        options={ searchOptions }
                        disabled={ searchDisabled }
                        notFoundContent={ searchNotFoundContent }
                        onChange={ handleSearchTextChange }
                        onSelect={ handleSearchSelect }>
                        <Search
                            allowClear
                            disabled={ searchDisabled }
                            loading={ searchLoading }
                            placeholder={ Localize({ children: "SEARCH.Input_Placeholder_CheckLocation" }).props.children }
                            onSearch={ handleSearchSubmit } />
                    </AutoComplete>
                </Form.Item>

                { searchUnavailableReasonKey &&
                    <div className="event-location-map__search-status event-location-map__search-status--error">
                        <Localize>{ searchUnavailableReasonKey }</Localize>
                    </div>
                }
            </div>

            <Row gutter={ 10 } className="event-location-map__inputs">
                <Col xs={ 24 } md={ 8 }>
                    <Form.Item
                        name="check_latitude"
                        label={ <Localize>FORMS.Input_Label_CheckLatitude</Localize> }
                        initialValue={ eventHasCoordinates ? event?.check_latitude : undefined }>
                        <InputNumber
                            min={ -90 }
                            max={ 90 }
                            step={ 0.000001 }
                            precision={ 6 }
                            controls={ false }
                            style={ { width: "100%" } } />
                    </Form.Item>
                </Col>

                <Col xs={ 24 } md={ 8 }>
                    <Form.Item
                        name="check_longitude"
                        label={ <Localize>FORMS.Input_Label_CheckLongitude</Localize> }
                        initialValue={ eventHasCoordinates ? event?.check_longitude : undefined }>
                        <InputNumber
                            min={ -180 }
                            max={ 180 }
                            step={ 0.000001 }
                            precision={ 6 }
                            controls={ false }
                            style={ { width: "100%" } } />
                    </Form.Item>
                </Col>

                <Col xs={ 24 } md={ 8 }>
                    <Form.Item
                        name="check_radius"
                        label={ <Localize>FORMS.Input_Label_CheckRadius</Localize> }
                        initialValue={ event?.check_radius ?? DEFAULT_CHECK_RADIUS }>
                        <InputNumber
                            min={ 1 }
                            max={ 100000 }
                            precision={ 0 }
                            controls={ false }
                            style={ { width: "100%" } } />
                    </Form.Item>
                </Col>
            </Row>

            <div className="event-location-map__map">
                <MapContainer
                    center={ center }
                    zoom={ hasCoordinates ? CHECK_ZOOM : DEFAULT_ZOOM }
                    scrollWheelZoom={ true }
                    className="event-location-map__leaflet">
                    <TileLayer
                        attribution="&copy; OpenStreetMap contributors"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapViewController
                        center={ center }
                        zoom={ hasCoordinates ? CHECK_ZOOM : DEFAULT_ZOOM } />
                    <MapSearchController target={ searchTarget } />
                    <MapClickHandler onMapClick={ handleMapClick } />

                    { hasCoordinates &&
                        <>
                            <Marker position={ center } />
                            { radiusValue > 0 &&
                                <Circle
                                    center={ center }
                                    radius={ radiusValue }
                                    pathOptions={ {
                                        color: "#ec008c",
                                        fillColor: "#ec008c",
                                        fillOpacity: 0.18,
                                    } } />
                            }
                        </>
                    }
                </MapContainer>
            </div>
        </div>
    );
};

export const EventLocationPreviewMap = ({ latitude, longitude, radius, points = [] }) => {
    const hasCoordinates = isValidLatitude(latitude) && isValidLongitude(longitude);
    const validPoints = useMemo(() => (
        points.filter((point) => isValidLatitude(point.latitude) && isValidLongitude(point.longitude))
    ), [ points ]);
    const hasPoints = validPoints.length > 0;
    const radiusValue = toNumber(radius);
    const pointPositions = useMemo(() => (
        validPoints.map((point) => [ toNumber(point.latitude), toNumber(point.longitude) ])
    ), [ validPoints ]);
    const center = hasCoordinates ?
        [ toNumber(latitude), toNumber(longitude) ] :
        (hasPoints ? pointPositions[ 0 ] : LEIPZIG_CENTER);
    const zoom = hasCoordinates || hasPoints ? CHECK_ZOOM : DEFAULT_ZOOM;

    return (
        <div className="event-location-preview-map">
            <MapContainer
                center={ center }
                zoom={ zoom }
                scrollWheelZoom={ true }
                className="event-location-preview-map__leaflet">
                <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                { hasPoints ?
                    <MapBoundsController positions={ pointPositions } /> :
                    <MapViewController center={ center } zoom={ zoom } />
                }

                { hasCoordinates &&
                    <>
                        <Marker position={ center } />
                        { radiusValue > 0 &&
                            <Circle
                                center={ center }
                                radius={ radiusValue }
                                pathOptions={ {
                                    color: "#ec008c",
                                    fillColor: "#ec008c",
                                    fillOpacity: 0.18,
                                } } />
                        }
                    </>
                }

                { hasPoints &&
                    <>
                        { pointPositions.length > 1 &&
                            <Polyline
                                positions={ pointPositions }
                                pathOptions={ { color: "#ec008c", weight: 3, opacity: 0.7 } } />
                        }

                        { validPoints.map((point) => {
                            const isCheckOutPoint = point.source === "check_out" || point.action === "check_out";

                            return (
                                <CircleMarker
                                    key={ point.id || `${ point.latitude }-${ point.longitude }-${ point.created_at }` }
                                    center={ [ toNumber(point.latitude), toNumber(point.longitude) ] }
                                    radius={ 6 }
                                    pathOptions={ {
                                        color: isCheckOutPoint ? "#1890ff" : "#ec008c",
                                        fillColor: isCheckOutPoint ? "#1890ff" : "#ec008c",
                                        fillOpacity: 0.65,
                                    } } />
                            );
                        }) }
                    </>
                }
            </MapContainer>
        </div>
    );
};

export default ExhibitionLocationMap;
